import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../services/api';
import { getCurrencySymbol } from '../utils/currency';
import DataTable from '../components/DataTable';
import IconButton from '../components/IconButton';
import './Locations.css';

function Locations({ tenantId }) {
  const navigate = useNavigate();
  const [locations, setLocations] = useState([]);
  const [tenant, setTenant] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingLocation, setEditingLocation] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    address: {
      street: '',
      city: '',
      zip: '',
      country: ''
    },
    monthlyRent: 0,
    description: ''
  });

  useEffect(() => {
    if (tenantId) {
      loadTenant();
      loadLocations();
    }
  }, [tenantId]);

  const loadTenant = async () => {
    try {
      const data = await api.get('/tenants', null);
      const currentTenant = data.find(t => t.id === tenantId) || data[0];
      setTenant(currentTenant);
    } catch (error) {
      console.error('Error loading tenant:', error);
    }
  };

  const currency = getCurrencySymbol(tenant?.taxSystem || 'DE');

  const loadLocations = async () => {
    try {
      const data = await api.get('/locations', tenantId);
      setLocations(data);
    } catch (error) {
      console.error('Error loading locations:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingLocation) {
        await api.put(`/locations/${editingLocation.id}`, formData, tenantId);
      } else {
        await api.post('/locations', formData, tenantId);
      }
      setShowForm(false);
      setEditingLocation(null);
      setFormData({
        name: '',
        address: { street: '', city: '', zip: '', country: '' },
        monthlyRent: 0,
        description: ''
      });
      loadLocations();
    } catch (error) {
      console.error('Error saving location:', error);
    }
  };

  const handleEdit = (location) => {
    setEditingLocation(location);
    setFormData({
      ...location,
      address: location.address || { street: '', city: '', zip: '', country: '' }
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Werkstattort wirklich löschen?')) {
      try {
        await api.delete(`/locations/${id}`, tenantId);
        loadLocations();
      } catch (error) {
        console.error('Error deleting location:', error);
      }
    }
  };

  const totalMonthlyRent = locations.reduce((sum, loc) => sum + (loc.monthlyRent || 0), 0);

  return (
    <div className="locations">
      <div className="page-header">
        <h1>Werkstattorte</h1>
        <div>
          <span style={{ marginRight: '1rem', color: '#666' }}>
            Gesamtmiete: <strong>{totalMonthlyRent.toFixed(2)} {currency}/Monat</strong>
          </span>
          <button type="button" onClick={() => setShowForm(true)} className="btn-primary">
            Neuer Werkstattort
          </button>
        </div>
      </div>

      {showForm && (
        <div className="modal">
          <div className="modal-content">
            <h2>{editingLocation ? 'Werkstattort bearbeiten' : 'Neuer Werkstattort'}</h2>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Name</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Beschreibung</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows="3"
                />
              </div>
              <div className="form-group">
                <label>Monatliche Miete ({currency})</label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.monthlyRent}
                  onChange={(e) => setFormData({ ...formData, monthlyRent: e.target.value === '' ? '' : e.target.value })}
                  onBlur={(e) => {
                    const val = e.target.value === '' ? 0 : parseFloat(e.target.value) || 0;
                    setFormData({ ...formData, monthlyRent: val });
                  }}
                  min="0"
                />
              </div>
              <h3>Adresse</h3>
              <div className="form-group">
                <label>Straße</label>
                <input
                  type="text"
                  value={formData.address.street}
                  onChange={(e) => setFormData({
                    ...formData,
                    address: { ...formData.address, street: e.target.value }
                  })}
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>PLZ</label>
                  <input
                    type="text"
                    value={formData.address.zip}
                    onChange={(e) => setFormData({
                      ...formData,
                      address: { ...formData.address, zip: e.target.value }
                    })}
                  />
                </div>
                <div className="form-group">
                  <label>Stadt</label>
                  <input
                    type="text"
                    value={formData.address.city}
                    onChange={(e) => setFormData({
                      ...formData,
                      address: { ...formData.address, city: e.target.value }
                    })}
                  />
                </div>
              </div>
              <div className="form-group">
                <label>Land</label>
                <input
                  type="text"
                  value={formData.address.country}
                  onChange={(e) => setFormData({
                    ...formData,
                    address: { ...formData.address, country: e.target.value }
                  })}
                />
              </div>
              <div className="form-actions">
                <button type="submit" className="btn-primary">
                  {editingLocation ? 'Speichern' : 'Erstellen'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowForm(false);
                    setEditingLocation(null);
                  }}
                  className="btn-secondary"
                >
                  Abbrechen
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <DataTable
        data={locations}
        columns={[
          {
            key: 'actions',
            header: '',
            sortable: false,
            width: '80px',
            render: (row) => (
              <div style={{ display: 'inline-flex', gap: '0.25rem', whiteSpace: 'nowrap' }}>
                <IconButton
                  icon="edit"
                  onClick={() => handleEdit(row)}
                  title="Bearbeiten"
                />
                <IconButton
                  icon="delete"
                  onClick={() => handleDelete(row.id)}
                  title="Löschen"
                  className="danger"
                />
              </div>
            )
          },
          {
            key: 'name',
            header: 'Name',
            sortType: 'string',
            accessor: (row) => row.name || '',
            render: (row) => (
              <button 
                className="link-button" 
                onClick={() => navigate(`/locations/${row.id}`)}
              >
                {row.name || ''}
              </button>
            )
          },
          {
            key: 'address',
            header: 'Adresse',
            sortType: 'string',
            render: (row) => {
              const addr = row.address || {};
              return `${addr.street || ''}${addr.street ? ', ' : ''}${addr.zip || ''} ${addr.city || ''}${addr.country ? `, ${addr.country}` : ''}`.trim();
            }
          },
          {
            key: 'monthlyRent',
            header: 'Monatliche Miete',
            sortType: 'number',
            align: 'right',
            accessor: (row) => {
              const rent = row.monthlyRent;
              return rent === null || rent === undefined || rent === '' ? 0 : (typeof rent === 'number' ? rent : parseFloat(rent) || 0);
            },
            render: (row) => {
              const rent = row.monthlyRent;
              if (rent === null || rent === undefined || rent === '') {
                return '-';
              }
              const rentValue = typeof rent === 'number' ? rent : parseFloat(rent);
              if (isNaN(rentValue) || rentValue <= 0) {
                return '-';
              }
              return `${rentValue.toFixed(2)} ${currency}`;
            }
          },
          {
            key: 'description',
            header: 'Beschreibung',
            sortType: 'string',
            accessor: (row) => row.description || ''
          }
        ]}
        defaultSort={{ key: 'name', direction: 'asc' }}
        emptyMessage="Keine Werkstattorte vorhanden"
      />
    </div>
  );
}

export default Locations;

